import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from "@angular/core";
import { PluginRegistry } from "common/editor/description.service";
import { ProcessDescriptionService } from "process/description/process-description.service";
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { TextInput } from "common/form/input/types/text-input";
import { RichTextInput } from "common/form/input/types/rich-text-input";
import { Subject, takeUntil } from "rxjs";
import { TranslateParams } from "common/translate/translate-params";
import { MainService } from "core/main.service";
import { ScreenSize } from "core/screensize";
import { distinctUntilChanged } from "rxjs/operators";

type Translation = stages.process.translate.Translation;

export interface Inputs {
	nameInput?: TextInput;
	descriptionInput?: RichTextInput;
	customAttributes?: Map<string, RichTextInput>;
}

export interface DescriptionIdentitySettings {
	beanType: string;
	beanId: string;
	beanIdentity: string;
	pv: string;
	workspaceId: string;
	pluginRegistry: PluginRegistry;
	processTypeIdent: string;
}

@Component({
	selector: "stages-edit-translations",
	templateUrl: "edit-translations.component.html",
	styleUrls: ["edit-translations.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditTranslationsComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<boolean>();

	private beanId?: string;
	translation!: Translation;
	_translateParams!: TranslateParams;

	@Input() set translateParams(translateParams: TranslateParams) {
		this._translateParams = translateParams;
		this.initForm();
	}

	@Output() readonly submitted = new EventEmitter<Translation>();

	@Output() readonly cancelled = new EventEmitter<Translation>();

	private readonly pluginRegistry: PluginRegistry;

	private descriptionIdentitySettings?: DescriptionIdentitySettings;

	identifierList: string[] = ["translate.language.a", "translate.language.b"];

	form: UntypedFormGroup = new UntypedFormGroup({});

	inputsByLanguage: Map<string, Inputs> = new Map<string, Inputs>();

	isDisabled = false;
	isLoading = false;

	_screenSize = ScreenSize.LARGE;
	isFullScreen = true;
	isFormInitialized = false;

	constructor(
		private descriptionService: ProcessDescriptionService,
		private mainService: MainService,
		private changeDetector: ChangeDetectorRef,
	) {
		this.pluginRegistry = this.descriptionService.getPluginRegistry();

		this.mainService.applicationState.screenSize
			.pipe(distinctUntilChanged(([prev], [current]) => prev !== current))
			.pipe(takeUntil(this.destroy$))
			.subscribe((screenSize) => {
				this._screenSize = screenSize;
				this.changeDetector.markForCheck();
				this.isFullScreen = this._screenSize !== ScreenSize.XSMALL && this._screenSize !== ScreenSize.XXSMALL;
			});
	}

	ngOnInit(): void {
		this.subscribeToLanguageIsDisabledCheck(this._translateParams);
	}

	private subscribeToLanguageIsDisabledCheck(translateParams: TranslateParams): void {
		translateParams.translation.languages.forEach((language) => {
			if (language.id) {
				this.form
					.get(language.id)!
					.valueChanges.pipe(takeUntil(this.destroy$))
					.subscribe(() => {
						this.isDisabled = this.form.get(language.id!)!.invalid && this.form.get(language.id!)!.dirty;
					});
			}
		});
	}

	private initForm(): void {
		if (this._translateParams.beanId === this.beanId && this._translateParams.translation === this.translation) {
			return;
		}

		this.isFormInitialized = false;
		this.beanId = this._translateParams.beanId;
		this.translation = this._translateParams.translation;

		this.form = new UntypedFormGroup({});
		this.inputsByLanguage = new Map<string, Inputs>();
		this.descriptionIdentitySettings = {
			beanType: this._translateParams.beanType,
			beanId: this._translateParams.beanId,
			beanIdentity: this._translateParams.beanIdentity,
			pv: this._translateParams.pv,
			workspaceId: this._translateParams.workspaceId,
			pluginRegistry: this.pluginRegistry,
			processTypeIdent: "process",
		};

		this.translation.languages.forEach((language) => {
			if (language.id) {
				this.inputsByLanguage.set(
					language.id,
					setUpFormAndInputs(
						this.translation.isIndexElement,
						this.translation.translatedElements[language.id],
						language.id,
						this._translateParams.workspaceLanguage,
						this.translation.customRichTextAttributeIdents,
						this.form,
						this.descriptionIdentitySettings,
					),
				);
			}
		});
		this.isFormInitialized = true;
	}

	private updateModel(translation: Translation): void {
		translation.languages.forEach((language) => {
			if (language.id) {
				const formGroup = this.form.get(language.id);
				if (formGroup) {
					if (this.inputsByLanguage.get(language.id)!.nameInput) {
						this.updateName(formGroup as UntypedFormGroup, language, translation);
					}
					const newDescription = formGroup.get(this.inputsByLanguage.get(language.id)!.descriptionInput!.key)!.value;

					if (translation.translatedElements[language.id].displayDescription.html !== newDescription.html) {
						translation.translatedElements[language.id].displayDescription = newDescription;
					}

					for (const ident of translation.customRichTextAttributeIdents) {
						this.updateCustomRichtextAttributes(formGroup, ident, language);
					}
				}
			}
		});
	}

	private updateName(
		formGroup: UntypedFormGroup,
		language: stages.Option,
		translation: stages.process.translate.Translation,
	): void {
		const newName = formGroup.get(this.inputsByLanguage.get(language.id!)!.nameInput!.key)!.value;
		if (translation.translatedElements[language.id!].name !== newName) {
			translation.translatedElements[language.id!].name = newName;
		}
	}

	private updateCustomRichtextAttributes(
		formGroup: AbstractControl<undefined, undefined>,
		customRichTextAttributeIdent: string,
		language: stages.Option,
	): void {
		const customAttributeKey = this.inputsByLanguage
			.get(language.id!)
			?.customAttributes?.get(customRichTextAttributeIdent)?.key;
		if (customAttributeKey) {
			const newCustomAttribute = formGroup.get(customAttributeKey)?.value;
			if (
				newCustomAttribute &&
				this.translation.translatedElements[language.id!].customRichTextAttributes[customRichTextAttributeIdent]
					.html !== newCustomAttribute.html
			)
				this.translation.translatedElements[language.id!].customRichTextAttributes[customRichTextAttributeIdent] =
					newCustomAttribute;
		}
	}

	updateIsLoading(isLoading: boolean): void {
		this.isLoading = isLoading;
	}

	save(translation: Translation): void {
		this.isLoading = true;
		this.updateModel(translation);
		this.submitted.emit(translation);
	}

	cancel(translation: Translation): void {
		this.cancelled.emit(translation);
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}
}

function setUpFormAndInputs(
	isIndexElement: boolean,
	translatedElement: stages.process.translate.TranslatedElement,
	language: string,
	workspaceLanguage: string,
	customRichTextAttributeIdents: string[],
	form: UntypedFormGroup,
	descriptionIdentitySettings?: DescriptionIdentitySettings,
): Inputs {
	const newForm = new UntypedFormGroup({});
	let nameInput: TextInput | undefined;
	let descriptionInput: RichTextInput | undefined;
	const customAttributeInputs = new Map<string, RichTextInput>();

	if (!isIndexElement) {
		nameInput = setupNameInput(translatedElement, language, workspaceLanguage, newForm);
	}

	if (descriptionIdentitySettings) {
		descriptionInput = setUpDescriptionInput(
			translatedElement.displayDescription,
			descriptionIdentitySettings,
			"description_" + language,
			"process.element.edit.description",
			newForm,
		);

		for (const customRichTextAttributeIdent of customRichTextAttributeIdents) {
			setupCustomRichtextInput(
				translatedElement,
				customRichTextAttributeIdent,
				descriptionIdentitySettings,
				language,
				newForm,
				customAttributeInputs,
			);
		}
	}

	form.addControl(language, newForm);

	return {
		nameInput: nameInput,
		descriptionInput: descriptionInput,
		customAttributes: customAttributeInputs,
	};
}

function setupNameInput(
	translatedElement: stages.process.translate.TranslatedElement,
	language: string,
	workspaceLanguage: string,
	newForm: UntypedFormGroup,
): TextInput {
	const nameInput = new TextInput({
		value: translatedElement.name,
		key: "name_" + language,
		translateKey: "process.element.edit.name",
		maxLength: 255,
	});

	const validators =
		workspaceLanguage === language
			? [Validators.maxLength(255), Validators.pattern(/^[^|]+$/), Validators.required]
			: [Validators.maxLength(255), Validators.pattern(/^[^|]+$/)];
	newForm.addControl(nameInput.key, new FormControl(nameInput.value, validators));

	if (workspaceLanguage === language) {
		newForm.get(nameInput.key)?.markAsTouched();
	}

	return nameInput;
}

function setUpDescriptionInput(
	displayDescription: unknown,
	settings: DescriptionIdentitySettings,
	key: string,
	translateKey: string,
	newForm: UntypedFormGroup,
): RichTextInput {
	const descriptionInput = new RichTextInput({
		key: key,
		value: displayDescription as string,
		pluginRegistry: settings.pluginRegistry,
		beanIdentity: settings.beanIdentity,
		beanId: settings.beanId,
		beanType: settings.beanType,
		workspaceId: settings.workspaceId,
		pv: settings.pv,
		processTypeIdent: settings.processTypeIdent,
		translateKey: translateKey,
		readonly: false,
		deactivateFloatingButton: true,
	});
	newForm.addControl(descriptionInput.key, new FormControl(descriptionInput.value));
	return descriptionInput;
}

function setupCustomRichtextInput(
	translatedElement: stages.process.translate.TranslatedElement,
	customRichTextAttributeIdent: string,
	descriptionIdentitySettings: DescriptionIdentitySettings,
	language: string,
	newForm: UntypedFormGroup,
	customAttributeInputs: Map<string, RichTextInput>,
): void {
	const customAttribute = translatedElement.customRichTextAttributes[customRichTextAttributeIdent];
	const customAttributeInput = setUpDescriptionInput(
		customAttribute,
		descriptionIdentitySettings,
		customRichTextAttributeIdent + "_" + language,
		"process.element.edit.customAttributes",
		newForm,
	);

	newForm.addControl(customAttributeInput.key, new FormControl(customAttributeInput.value));
	customAttributeInputs.set(customRichTextAttributeIdent, customAttributeInput);
}
